import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface Info {
  hotel: { name: string; logo?: string | null };
  booking: {
    bookingid: string; guestname: string | null;
    checkin: string | null; checkout: string | null; nights: number | null;
    currency: string; status: string; meal_plan: string | null;
    payment_link: string | null; already_paid: boolean;
  };
  breakdown: {
    rooms: { roomid: string; subtotal: number }[];
    subtotal: number; tax: number; service: number; total: number; deposit: number;
    tax_rate: number; service_rate: number; deposit_rate: number;
  };
}

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
const money = (n: number, cur: string) =>
  `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;

export default function PaymentRedirect() {
  const [params] = useSearchParams();
  // `ref` arrives as ?ref= on the WhatsApp link. On Paymob's post-payment
  // response callback it appends ?merchant_order_id= instead — accept either.
  const ref = params.get("ref") || params.get("merchant_order_id");
  const uid = params.get("uid");

  const [info, setInfo]       = useState<Info | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!ref || !uid) { setError("Invalid payment link."); setLoading(false); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/payment/info`, { params: { ref, uid } });
        setInfo(data);
      } catch (e: any) {
        setError(e.response?.data?.error || "Could not load your booking. Please contact the hotel.");
      } finally { setLoading(false); }
    })();
  }, [ref, uid]);

  const pay = () => {
    if (!info?.booking.payment_link) return;
    setRedirecting(true);
    window.location.href = info.booking.payment_link;
  };

  const cur = info?.booking.currency || "EGP";

  // Paymob appends ?success=true&amount_cents=… to the redirect URL after payment.
  // Trust that immediately so the guest sees confirmation without waiting on the webhook.
  const paidViaRedirect = params.get("success") === "true";
  const paidAmtCents = params.get("amount_cents");
  const amountPaid = paidAmtCents ? Number(paidAmtCents) / 100 : (info?.breakdown.deposit ?? 0);
  const isPaid = paidViaRedirect || !!info?.booking.already_paid;

  return (
    <div style={S.page}>
      <div style={S.card}>
        {/* Logo header — only for hotels with a configured logo */}
        {info?.hotel.logo && (
          <div style={S.logoHeader}>
            <img src={info.hotel.logo} alt={info.hotel.name} style={S.logoImg} />
          </div>
        )}

        {loading && <div style={S.muted}>Loading your booking…</div>}

        {/* Error — but never over a successful payment redirect */}
        {error && !loading && !paidViaRedirect && (
          <div style={S.errorBox}>{error}</div>
        )}

        {/* Confirmation — shown on a Paymob success redirect (or already-paid booking),
            even if the booking lookup failed, so the guest always sees their payment landed. */}
        {isPaid && !loading && (
          <div style={S.successWrap}>
            <div style={S.successBadge}>✓</div>
            <div style={S.successTitle}>Payment received!</div>
            <div style={S.successSub}>Your booking is confirmed — see you soon 🌿</div>

            {/* Booking summary (whatever we could load) */}
            <div style={{ ...S.section, marginTop: 20 }}>
              <Row label="Booking" value={info?.booking.bookingid || ref || "—"} mono />
              {info?.booking.guestname && <Row label="Guest" value={info.booking.guestname} />}
              {info?.booking.checkin && <Row label="Check-in"  value={fmtDate(info.booking.checkin)} />}
              {info?.booking.checkout && <Row label="Check-out" value={fmtDate(info.booking.checkout)} />}
              {info?.booking.nights != null && <Row label="Nights" value={String(info.booking.nights)} />}
              {info?.booking.meal_plan && <Row label="Meal plan" value={info.booking.meal_plan} />}
            </div>

            {/* Amount paid */}
            {amountPaid > 0 && (
              <div style={S.depositBox}>
                <div style={S.depositLabel}>Amount paid</div>
                <div style={S.depositAmt}>{money(amountPaid, cur)}</div>
              </div>
            )}

            <div style={S.secure}>💬 A confirmation has also been sent to you on WhatsApp</div>
          </div>
        )}

        {/* Pay flow — only when loaded, not yet paid */}
        {info && !loading && !isPaid && (
          <>
            {/* Booking summary */}
            <div style={S.section}>
              <Row label="Booking" value={info.booking.bookingid} mono />
              {info.booking.guestname && <Row label="Guest" value={info.booking.guestname} />}
              <Row label="Check-in"  value={fmtDate(info.booking.checkin)} />
              <Row label="Check-out" value={fmtDate(info.booking.checkout)} />
              <Row label="Nights"    value={String(info.booking.nights ?? "—")} />
              {info.booking.meal_plan && <Row label="Meal plan" value={info.booking.meal_plan} />}
            </div>

            {/* Price breakdown */}
            <div style={S.section}>
              <Row label="Subtotal" value={money(info.breakdown.subtotal, cur)} />
              <Row label={`Tax (${Math.round(info.breakdown.tax_rate * 100)}%)`} value={money(info.breakdown.tax, cur)} />
              <Row label={`Service (${Math.round(info.breakdown.service_rate * 100)}%)`} value={money(info.breakdown.service, cur)} />
              <div style={S.divider} />
              <Row label="Total" value={money(info.breakdown.total, cur)} bold />
            </div>

            {/* Deposit highlight */}
            <div style={S.depositBox}>
              <div style={S.depositLabel}>Deposit to pay now ({Math.round(info.breakdown.deposit_rate * 100)}%)</div>
              <div style={S.depositAmt}>{money(info.breakdown.deposit, cur)}</div>
            </div>

            {/* Pay button */}
            {info.booking.payment_link ? (
              <button style={{ ...S.payBtn, ...(redirecting ? S.payBtnBusy : {}) }} onClick={pay} disabled={redirecting}>
                {redirecting ? "Redirecting to secure payment…" : "Pay deposit securely →"}
              </button>
            ) : (
              <div style={S.errorBox}>
                Payment link not ready yet. Please reply to the hotel on WhatsApp and we'll resend it.
              </div>
            )}

            <div style={S.secure}>🔒 Secured by Paymob · Your card details never touch our servers</div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, bold, mono }: { label: string; value: string; bold?: boolean; mono?: boolean }) {
  return (
    <div style={S.row}>
      <span style={S.rowLabel}>{label}</span>
      <span style={{ ...S.rowValue, ...(bold ? { fontWeight: 800, fontSize: 16 } : {}), ...(mono ? { fontFamily: "monospace", fontSize: 12 } : {}) }}>{value}</span>
    </div>
  );
}

const GOLD = "#C9A84C";
const S: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "linear-gradient(160deg, #080F1E 0%, #0E1E38 100%)", fontFamily: "'Raleway', system-ui, sans-serif" },
  card: { width: "100%", maxWidth: 420, background: "#0E1E38", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 18, padding: 28, boxShadow: "0 20px 60px rgba(0,0,0,0.5)", color: "#F2EBD9" },
  logoHeader: { display: "flex", justifyContent: "center", marginBottom: 22, paddingBottom: 18, borderBottom: "1px solid rgba(201,168,76,0.15)" },
  logoImg: { maxHeight: 64, maxWidth: 220, objectFit: "contain" },
  header: { display: "flex", alignItems: "center", gap: 12, marginBottom: 22 },
  logo: { width: 46, height: 46, borderRadius: 12, background: "rgba(201,168,76,0.14)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 },
  hotelName: { fontSize: 18, fontWeight: 800, color: "#FDFAF4", fontFamily: "'Cormorant Garamond', serif" },
  tagline: { fontSize: 12, color: "rgba(242,235,217,0.5)" },
  muted: { color: "rgba(242,235,217,0.5)", fontSize: 14, textAlign: "center", padding: "30px 0" },
  section: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "12px 16px", marginBottom: 14 },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", fontSize: 13.5 },
  rowLabel: { color: "rgba(242,235,217,0.55)" },
  rowValue: { color: "#F2EBD9", fontWeight: 600 },
  divider: { height: 1, background: "rgba(201,168,76,0.15)", margin: "8px 0" },
  depositBox: { background: "linear-gradient(135deg, rgba(201,168,76,0.18), rgba(232,201,122,0.1))", border: `1px solid ${GOLD}55`, borderRadius: 12, padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 },
  depositLabel: { fontSize: 12, color: "rgba(242,235,217,0.7)", fontWeight: 600 },
  depositAmt: { fontSize: 20, fontWeight: 800, color: GOLD },
  payBtn: { width: "100%", padding: "14px", borderRadius: 11, border: "none", background: `linear-gradient(135deg, ${GOLD}, #E8C97A)`, color: "#080F1E", fontSize: 15, fontWeight: 800, cursor: "pointer", transition: "opacity 0.15s" },
  payBtnBusy: { opacity: 0.6, cursor: "default" },
  secure: { textAlign: "center", fontSize: 11, color: "rgba(242,235,217,0.4)", marginTop: 14 },
  errorBox: { background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 10, padding: "12px 14px", color: "#fca5a5", fontSize: 13, textAlign: "center" },
  paidBox: { background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.3)", borderRadius: 10, padding: "16px", color: "#6ee7b7", fontSize: 14, textAlign: "center", lineHeight: 1.5 },
  successWrap: { textAlign: "center" },
  successBadge: { width: 64, height: 64, borderRadius: "50%", margin: "4px auto 14px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, fontWeight: 800, color: "#080F1E", background: "linear-gradient(135deg, #34d399, #6ee7b7)", boxShadow: "0 8px 24px rgba(52,211,153,0.35)" },
  successTitle: { fontSize: 22, fontWeight: 800, color: "#FDFAF4", fontFamily: "'Cormorant Garamond', serif" },
  successSub: { fontSize: 13.5, color: "rgba(242,235,217,0.6)", marginTop: 4 },
};
